import { StockData } from '@/types';
import { ClientDealSummary, EnhancedStockData } from '@/types/deals';

/**
 * Aggregates deal data by client, calculating total purchase and sell quantities
 */
export function aggregateClientDeals(stock: StockData): ClientDealSummary[] {
  if (!stock.deals || stock.deals.length === 0) {
    return [];
  }

  const clientMap = new Map<string, ClientDealSummary>();

  stock.deals.forEach(deal => {
    const clientName = deal.client_name;
    
    if (!clientMap.has(clientName)) {
      clientMap.set(clientName, {
        clientName,
        totalPurchaseQuantity: 0,
        totalSellQuantity: 0,
        totalPurchaseValue: 0,
        totalSellValue: 0,
        dealDates: new Set<string>()
      });
    }

    const summary = clientMap.get(clientName)!;
    const dealValue = deal.average_price * deal.quantity;
    
    // Add the date to track unique dates
    summary.dealDates.add(new Date(deal.reported_date).toISOString().split('T')[0]);
    
    if (deal.action === 'Purchase') {
      summary.totalPurchaseQuantity += deal.quantity;
      summary.totalPurchaseValue += dealValue;
    } else if (deal.action === 'Sell') {
      summary.totalSellQuantity += deal.quantity;
      summary.totalSellValue += dealValue;
    }
  });

  return Array.from(clientMap.values());
}

/**
 * Enhances stock data with aggregated client deal information
 */
export function enhanceStocksWithDealSummaries(stocks: StockData[]): EnhancedStockData[] {
  return stocks.map(stock => {
    const clientDealSummaries = aggregateClientDeals(stock);
    const transformedDeals = stock.deals?.map(deal => ({
      ...deal,
      post_transaction_holding: deal.post_transaction_holding === null ? undefined : deal.post_transaction_holding
    }));
    
    return {
      ...stock,
      clientDealSummaries,
      deals: transformedDeals
    };
  });
} 